import { Badge } from "@/components/ui/badge"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Calendar, AlertTriangle, CheckCircle, Clock } from "lucide-react"
import { format } from "date-fns"

interface ReviewStatusBadgeProps {
  nextReviewDate: Date | null
  isOverdue: boolean
  daysUntilDue?: number | null
  lastReviewedAt?: Date | null
  reviewCount?: number
  showTooltip?: boolean
}

export function ReviewStatusBadge({
  nextReviewDate,
  isOverdue,
  daysUntilDue,
  lastReviewedAt,
  reviewCount = 0,
  showTooltip = true
}: ReviewStatusBadgeProps) {
  if (!nextReviewDate) {
    return null
  }

  // Determine status and styling
  const getStatusInfo = () => {
    if (isOverdue) {
      return {
        label: `Overdue ${Math.abs(daysUntilDue || 0)}d`,
        variant: "destructive" as const,
        icon: AlertTriangle,
        className: "bg-red-600 text-white hover:bg-red-700",
        tooltipColor: "text-red-600"
      }
    }

    if (daysUntilDue !== null && daysUntilDue !== undefined) {
      if (daysUntilDue === 0) {
        return {
          label: "Due Today",
          variant: "default" as const,
          icon: AlertTriangle,
          className: "bg-orange-500 text-white hover:bg-orange-600",
          tooltipColor: "text-orange-600"
        }
      }

      if (daysUntilDue <= 7) {
        return {
          label: `Due in ${daysUntilDue}d`,
          variant: "secondary" as const,
          icon: Clock,
          className: "bg-amber-500 text-white hover:bg-amber-600",
          tooltipColor: "text-amber-600"
        }
      }

      // Use blue tones for upcoming reviews to differentiate from workflow status (green = approved)
      return {
        label: `Due in ${daysUntilDue}d`,
        variant: "outline" as const,
        icon: Calendar,
        className: "bg-sky-100 text-sky-700 border-sky-300 hover:bg-sky-200 dark:bg-sky-950/30 dark:text-sky-300 dark:border-sky-700",
        tooltipColor: "text-sky-600"
      }
    }

    return {
      label: "Review Scheduled",
      variant: "outline" as const,
      icon: Calendar,
      className: "bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800",
      tooltipColor: "text-blue-600"
    }
  }

  const statusInfo = getStatusInfo()
  const Icon = statusInfo.icon

  const badge = (
    <Badge 
      variant={statusInfo.variant}
      className={`inline-flex items-center space-x-1 w-fit ${statusInfo.className}`}
    >
      <Icon className="h-3 w-3" />
      <span>{statusInfo.label}</span>
    </Badge>
  )

  if (!showTooltip) {
    return badge
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Calendar className={`h-4 w-4 ${statusInfo.tooltipColor}`} />
              <span className="font-semibold">Review Information</span>
            </div>
            <div className="text-sm space-y-1">
              <p>
                <span className="text-muted-foreground">Next Review:</span>{" "}
                <span className="font-medium">{format(new Date(nextReviewDate), 'PPP')}</span>
              </p>
              {lastReviewedAt && (
                <p>
                  <span className="text-muted-foreground">Last Reviewed:</span>{" "}
                  <span className="font-medium">{format(new Date(lastReviewedAt), 'PPP')}</span>
                </p>
              )}
              <p>
                <span className="text-muted-foreground">Review Count:</span>{" "}
                <span className="font-medium">{reviewCount}</span>
              </p>
              {isOverdue && (
                <p className="text-red-600 font-medium mt-2">
                  ⚠️ This assessment is overdue for review
                </p>
              )}
              {daysUntilDue !== null && daysUntilDue !== undefined && daysUntilDue <= 7 && !isOverdue && (
                <p className="text-yellow-600 font-medium mt-2">
                  ⏰ Review due soon
                </p>
              )}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
